import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import axios from 'axios';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { useTranslations } from '../context/TranslationsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback category names if API fails
const fallbackCategoryNames = {
  pl: {
    general: 'Ogólne',
    booking: 'Rezerwacje',
    passes: 'Karnety',
    birthdays: 'Urodziny',
    classes: 'Zajęcia',
    other: 'Inne'
  },
  en: {
    general: 'General',
    booking: 'Bookings',
    passes: 'Passes',
    birthdays: 'Birthdays',
    classes: 'Classes',
    other: 'Other'
  }
};

const FAQPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['faq', 'common']);
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };
  
  const [faqItems, setFaqItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [faqRes, catRes] = await Promise.all([
        axios.get(`${API}/faq`),
        axios.get(`${API}/admin/faq-categories`).catch(() => ({ data: [] }))
      ]);
      setFaqItems(faqRes.data);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get category name from API data or fallback
  const getCategoryName = (categorySlug) => {
    // First try to find in API categories
    const apiCategory = categories.find(c => c.slug === categorySlug || c.category_id === categorySlug);
    if (apiCategory?.name) {
      return apiCategory.name[lang] || apiCategory.name.pl || categorySlug;
    }
    // Fallback to hardcoded names
    return fallbackCategoryNames[lang]?.[categorySlug] || fallbackCategoryNames.pl[categorySlug] || categorySlug;
  };

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqItems(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and group FAQ by category
  const { filteredItems, groupedByCategory } = useMemo(() => {
    // Filter by search query
    let filtered = faqItems;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = faqItems.filter(item => {
        const question = (item.question?.[lang] || item.question?.pl || '').toLowerCase();
        const answer = (item.answer?.[lang] || item.answer?.pl || '').toLowerCase();
        return question.includes(query) || answer.includes(query);
      });
    }

    // Group by category
    const grouped = filtered.reduce((acc, item) => {
      const category = item.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});

    // Sort categories: use API category order if available, otherwise alphabetical
    const categoryOrder = categories.reduce((acc, cat, idx) => {
      acc[cat.slug || cat.category_id] = cat.sortOrder ?? idx;
      return acc;
    }, {});
    
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      const orderA = categoryOrder[a] ?? 999;
      const orderB = categoryOrder[b] ?? 999;
      return orderA - orderB;
    });

    return {
      filteredItems: filtered,
      groupedByCategory: sortedCategories.map(cat => ({ category: cat, items: grouped[cat] }))
    };
  }, [faqItems, searchQuery, lang, categories]);

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 md:px-12" data-testid="faq-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-['Syne'] font-bold mb-4">{t('faq.title', 'FAQ')}</h1>
          <p className="text-muted-foreground">
            {t('faq.subtitle', lang === 'pl' ? 'Znajdź odpowiedzi na najczęściej zadawane pytania' : 'Find answers to frequently asked questions')}
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input
            type="text"
            placeholder={t('faq.searchPlaceholder', lang === 'pl' ? 'Szukaj w FAQ...' : 'Search FAQ...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-6 text-lg glass-card border-0"
            data-testid="faq-search"
          />
        </div>

        {loading ? (
          <div className="space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-8 w-32" />
                <div className="space-y-3">
                  {[1, 2, 3].map((j) => (
                    <Skeleton key={j} className="h-14 w-full" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {lang === 'pl' ? 'Nie znaleziono wyników' : 'No results found'}
          </div>
        ) : (
          <div className="space-y-16">
            {groupedByCategory.map(({ category, items }) => (
              <div key={category} data-testid={`faq-category-${category}`} className="pt-4 mb-8">
                {/* Category Header */}
                <h2 className="text-2xl font-bold mb-8 text-primary border-b border-primary/20 pb-4">
                  {getCategoryName(category)}
                </h2>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {items.map((item, index) => (
                    <AccordionItem
                      key={item.id || `${category}-${index}`}
                      value={item.id || `${category}-item-${index}`}
                      className="glass-card px-6 border-0"
                      data-testid={`faq-item-${category}-${index}`}
                    >
                      <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6">
                        {item.question?.[lang] || item.question?.pl}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-6">
                        {item.answer?.[lang] || item.answer?.pl}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQPage;
